import { useEffect, useState } from "react";
import { Check, Loader2, Paperclip, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { type ChecklistItem } from "@/services/task-checklist.service";
import { reportError } from "../utils";
import { ChecklistItemAttachmentsDialog } from "./ChecklistItemAttachmentsDialog";

export function ChecklistItemRow({
	projectId,
	taskId,
	item,
	canEdit,
	compact = false,
	onToggle,
	onRename,
	onRemove,
	onAttachmentsChanged,
}: {
	projectId: string;
	taskId: string;
	item: ChecklistItem;
	canEdit: boolean;
	compact?: boolean;
	onToggle: (item: ChecklistItem) => Promise<void>;
	onRename: (item: ChecklistItem, title: string) => Promise<void>;
	onRemove: (item: ChecklistItem) => Promise<void>;
	onAttachmentsChanged?: () => void;
}) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(item.title);
	const [saving, setSaving] = useState(false);
	const [attachmentsOpen, setAttachmentsOpen] = useState(false);

	const count = item.attachment_count ?? 0;
	const showAttachmentButton = canEdit || count > 0;

	useEffect(() => {
		setDraft(item.title);
	}, [item.title]);

	async function commit() {
		const next = draft.trim();
		if (!next || next === item.title) {
			setDraft(item.title);
			setEditing(false);
			return;
		}
		setSaving(true);
		try {
			await onRename(item, next);
			setEditing(false);
		} catch (e) {
			reportError(e);
			setDraft(item.title);
			setEditing(false);
		} finally {
			setSaving(false);
		}
	}

	function cancel() {
		setDraft(item.title);
		setEditing(false);
	}

	return (
		<div
			className={`flex gap-2.5 group group/item ${editing ? "items-center" : "items-start"}`}
		>
			<button
				type="button"
				onClick={() => canEdit && onToggle(item).catch(reportError)}
				disabled={!canEdit}
				aria-label={item.is_completed ? "Mark not done" : "Mark done"}
				className={`h-4 w-4 rounded shrink-0 flex items-center justify-center border transition-colors ${editing ? "" : "mt-0.5"} ${
					item.is_completed
						? "bg-primary border-primary text-white"
						: "border-border-strong bg-surface hover:border-primary"
				}`}
			>
				{item.is_completed && <Check className="h-3 w-3" />}
			</button>

			{editing ? (
				<Input
					autoFocus
					value={draft}
					disabled={saving}
					onChange={(e) => setDraft(e.target.value)}
					onBlur={commit}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							commit();
						} else if (e.key === "Escape") {
							e.preventDefault();
							e.stopPropagation();
							cancel();
						}
					}}
					className="h-7 text-sm flex-1"
				/>
			) : (
				<button
					type="button"
					onClick={() => canEdit && setEditing(true)}
					className={`flex-1 min-w-0 text-left text-sm leading-5 break-words transition-[color,max-height] ${
						compact
							? "line-clamp-1 max-h-5 overflow-hidden duration-300 ease-out group-hover/item:line-clamp-none group-hover/item:max-h-40 group-hover/item:delay-150 focus-visible:line-clamp-none focus-visible:max-h-40"
							: ""
					} ${
						item.is_completed
							? "line-through text-muted"
							: "text-foreground"
					} ${canEdit ? "cursor-text hover:text-primary" : ""}`}
				>
					{item.title}
				</button>
			)}

			{saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />}

			{showAttachmentButton && !editing && (
				<button
					type="button"
					onClick={() => setAttachmentsOpen(true)}
					aria-label={
						count > 0
							? `View ${count} attachment${count > 1 ? "s" : ""}`
							: "Add attachments"
					}
					title={
						count > 0
							? `${count} attachment${count > 1 ? "s" : ""}`
							: "Add attachments"
					}
					className={`flex items-center gap-0.5 shrink-0 mt-0.5 transition-colors ${
						count > 0
							? "text-muted hover:text-primary"
							: "opacity-0 group-hover:opacity-100 text-muted hover:text-primary"
					}`}
				>
					<Paperclip className="h-3.5 w-3.5" />
					{count > 0 && (
						<span className="text-[11px] font-medium">{count}</span>
					)}
				</button>
			)}

			{canEdit && !editing && (
				<button
					type="button"
					onClick={() => onRemove(item).catch(reportError)}
					aria-label="Delete item"
					className="opacity-0 group-hover:opacity-100 text-muted hover:text-destructive transition-opacity shrink-0 mt-0.5"
				>
					<Trash2 className="h-3.5 w-3.5" />
				</button>
			)}

			{attachmentsOpen && (
				<ChecklistItemAttachmentsDialog
					projectId={projectId}
					taskId={taskId}
					item={item}
					canEdit={canEdit}
					open={attachmentsOpen}
					onOpenChange={setAttachmentsOpen}
					onChanged={onAttachmentsChanged}
				/>
			)}
		</div>
	);
}
